import React, { useMemo, useState } from 'react';
import { FaEdit } from 'react-icons/fa';
import { FiLink, FiSave, FiUserPlus } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import ShoppingCartSVG from '../assets/ShoppingCartSVG';
import { Badge, Button, message, Tooltip } from 'antd';
import { CgTrash } from 'react-icons/cg';
import { setCustomerInfo, setWholeCart } from '../ShoppingCartSliceV2';
import { useCreateOrderApi } from '../../Checkout/CheckoutApis';
import useSupplierCancellationPolicyConfirm from '../hooks/useSupplierCancellationPolicyConfirm';
import useIsraelLawAgreement from '../hooks/useIsraelLawAgreement';
import useScreenWidth from '../../../hooks/useScreenSize';
import './shoppingCartHeader.mobile.less';

interface Props {
  diamondsCount?: any;
  shoppingCartOpen: boolean;
  checkout: boolean;
  setShoppingCartOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAddUserModal: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      customer_secret: string;
    }>
  >;
}

const ShoppingCartHeader = ({
  shoppingCartOpen,
  checkout,
  setShoppingCartOpen,
  setAddUserModal,
  diamondsCount = 0
}: Props) => {
  const { shoppingCartTranslations: t } = useSelector(
    (data: RootState) => data.config.translations
  );

  const { freePackage } = useSelector((data: RootState) => data.shoppingCart);

  const { siteConfig } = useSelector((data: RootState) => data.config.config);
  const { customerInfo } = useSelector(
    (data: RootState) => data.shoppingCartV2
  );
  const { agentMode } = useSelector((data: RootState) => data.config.agentInfo);
  const {
    wallet: t_wallet,
  } = useSelector((data: RootState) => data.config.translations);
  const handleEditUser = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAddUserModal({
      open: true,
      customer_secret: customerInfo.customer_secret,
    });
  };

  const handleAssignUser = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAddUserModal({ open: true, customer_secret: '' });
  };

  const dispatch = useDispatch();

  const handleClearCartAndCurrentUser = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setWholeCart({}));
    dispatch(
      setCustomerInfo({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        comment: '',
        secret: '',
      })
    );
    localStorage.removeItem('currentCustomer');
  };
  const { ShoppingCartV2: shoppingCart, agent_commission, attachToOrder } =
    useSelector((state: RootState) => state.shoppingCartV2);

  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const createOrderApi = useCreateOrderApi();
  const { confirmSaveOrder, showOfferNotice } =
    useSupplierCancellationPolicyConfirm();
  const { cartHasIsraelLawAgreement } = useIsraelLawAgreement();
  const hasIsraelLawAgreement = cartHasIsraelLawAgreement(shoppingCart);

  const handleCreateOrder = (e: React.MouseEvent) => {
    e.stopPropagation();
    // While attaching to an existing order the cart must not be
    //saved as a NEW order — the products get attached at checkout instead
    if (attachToOrder) {
      message.info(`${t.attachCompleteFromCheckout} (#${attachToOrder.order_id})`);
      return;
    }
    // The offer is not saved until the agent confirms the Israeli-law
    // (supplier cancellation policy) products listed in userAgreements
    if (hasIsraelLawAgreement) {
      confirmSaveOrder(createOrder);
      return;
    }
    createOrder();
  };

  const createOrder = async () => {
    try {
      const responses = await createOrderApi(
        {
          ...(shoppingCart ?? {}),
          agent_commission: { ...agent_commission, is_commission_per_order: 1 },
        },
        setIsCreateLoading
      );

      if (responses.data.status == 'success') {
        dispatch(
          setWholeCart({
            ...(shoppingCart ?? {}),
            agent_commission: agent_commission,
            orderSecret: String(responses.data.data.secret),
            isSaved: true,
            orderId: String(responses.data.data.order_id),
          })
        );
      } else {
        message.error(t.createOrderError);
      }
    } catch {
      message.error(t.createOrderError);
    }
  };

  const handleCopyOrder = () => {
    const orderURL =
      window.location.origin + `/order/manage?id=${shoppingCart?.orderSecret}`;
    navigator.clipboard
      .writeText(orderURL)
      .then((r) => {
        message.success('קישור הועתק בהצלחה');
      })
      .catch((e) => {
        window.parent.postMessage({ action: 'copy', text: orderURL }, '*');
        //message.warning("לא ניתן להעתיק קישור");
      });
    // The saved offer still holds Israeli-law products: copying runs as
    // usual, plus the same notice — informative only, nothing to confirm
    if (hasIsraelLawAgreement) {
      showOfferNotice();
    }
  };

  const isShowCreateOrderButton =
    !checkout && agentMode && customerInfo?.first_name && shoppingCart?.orderId;
  const isShowCreateUserButton =
    !checkout && agentMode && !customerInfo.first_name;

  // Phones: the agent tools get their own row under the title —
  //   Your cart                          💎 120   ✕
  //   ┌ (TN) Tazo Nebieridze  ✎ ┐ [🗑]          customer card · clear
  //   │      050-1234567        │
  //   [ 💾 Save offer ]  /  [ 🔗 Copy link ]    full-width action
  // (no customer yet: one dashed "+ Assign customer" button instead)
  const { isMobileWidth } = useScreenWidth();
  // saved customers may carry the API's customer_* field names
  const customerPhone =
    customerInfo?.phone ||
    (customerInfo as { customer_phone?: string })?.customer_phone;
  const customerInitials = [customerInfo?.first_name, customerInfo?.last_name]
    .map((part) => part?.trim()?.charAt(0) ?? '')
    .join('')
    .toUpperCase();
  const showDiamonds =
    agentMode && siteConfig?.shareB2C?.cashback?.is_active && diamondsCount > 0;

  // const selectedProductsCount = useMemo(() => {
  //   let count = 0;
  //   if (Array.isArray(ShoppingCartV2)) {
  //     count = ShoppingCartV2.reduce((sum, item) => {
  //       let valueToAdd = 0;
  //       item?.products?.forEach((prod: any) => {

  //         valueToAdd += prod?.subProducts?.length || 1;
  //       });

  //       return sum + valueToAdd;
  //     }, 0);
  //   } else {
  //     ShoppingCartV2?.products?.forEach((prod: any) => {
  //       count += prod?.subProducts?.length || 1;
  //     });
  //   }
  //   return count;
  // }, [ShoppingCartV2]);

  return (
    <div
      className={`header ${!shoppingCartOpen && 'header-closed'}`}
      onClick={() => !checkout && setShoppingCartOpen((prev) => !prev)}
    >
      {/* {
        siteConfig?.logoSmall ? <img src={siteConfig?.logoSmall} className="logo" /> :<LogoSvg className="logo"/>
      } */}
      {/* <Badge count={selectedProductsCount}> */}
      <ShoppingCartSVG className="shopping-cart-svg" />
      {/* </Badge> */}
      {shoppingCartOpen && isMobileWidth ? (
        <>
          <div className="cart-title">
            <span className="cart-title__text">
              {freePackage ? t.selectedItems : t?.shoppingCart}
            </span>
            {showDiamonds ? (
              <span
                id="obs-cart-diamonds"
                className="cart-diamonds"
                title={t_wallet.TotalDiamondsYouWillReceive}
              >
                <span aria-hidden="true">💎</span>
                {diamondsCount}
              </span>
            ) : null}
          </div>
          {agentMode && !checkout && (
            <div
              className="cart-agent-bar"
              onClick={(e) => e.stopPropagation()}
            >
              {customerInfo.first_name ? (
                <div id="obs-cart-customer" className="cart-customer">
                  <button
                    type="button"
                    className="cart-customer__main"
                    onClick={handleEditUser}
                  >
                    <span className="cart-customer__avatar">
                      {customerInitials}
                    </span>
                    <span className="cart-customer__text">
                      <span className="cart-customer__name">
                        {customerInfo.first_name} {customerInfo.last_name}
                      </span>
                      {customerPhone ? (
                        <span className="cart-customer__meta" dir="ltr">
                          {customerPhone}
                        </span>
                      ) : null}
                    </span>
                    <FaEdit className="cart-customer__edit" />
                  </button>
                  <button
                    type="button"
                    className="cart-customer__clear"
                    aria-label={t.clearCart}
                    title={t.clearCart}
                    onClick={handleClearCartAndCurrentUser}
                  >
                    <CgTrash />
                  </button>
                </div>
              ) : (
                <button
                  id="obs-cart-assign-customer"
                  type="button"
                  className="cart-assign"
                  onClick={handleAssignUser}
                >
                  <FiUserPlus />
                  {t.assignCustomer}
                </button>
              )}
              {isShowCreateOrderButton && (
                <Button
                  id="obs-cart-create-order"
                  type="primary"
                  block
                  className="cart-offer-btn"
                  icon={shoppingCart?.orderSecret ? <FiLink /> : <FiSave />}
                  loading={isCreateLoading}
                  onClick={(e) =>
                    shoppingCart?.orderSecret
                      ? handleCopyOrder()
                      : handleCreateOrder(e)
                  }
                >
                  {shoppingCart?.orderSecret ? t.copyOrderLink : t.createOrder}
                </Button>
              )}
            </div>
          )}
        </>
      ) : shoppingCartOpen ? (
        <>
          <div className="cart-title">
            {freePackage ? t.selectedItems : t?.shoppingCart}
            {isShowCreateOrderButton && (
              <Button
                id="obs-cart-create-order"
                className="btn-style-5 create-order-button"
                onClick={
                  shoppingCart?.orderSecret
                    ? handleCopyOrder
                    : handleCreateOrder
                }
                loading={isCreateLoading}
              >
                {shoppingCart?.orderSecret ? t.copyOrderLink : t.createOrder}
              </Button>
            )}
          </div>
          {customerInfo.first_name && agentMode && !checkout && (
            <div id="obs-cart-customer" className="customer-name" onClick={handleEditUser}>
              <FaEdit className="edit-user" />
              {customerInfo.first_name} {customerInfo.last_name}
            </div>
          )}
          {isShowCreateUserButton && (
            <Button
              id="obs-cart-assign-customer"
              className="btn-style-5 assign-customer-button"
              onClick={handleAssignUser}
            >
              {t.assignCustomer}
            </Button>
          )}
          {agentMode && customerInfo.first_name && !checkout && (
            <Tooltip title={t.clearCart}>
              <CgTrash
                className="clear-cart"
                size={25}
                onClick={(e) => handleClearCartAndCurrentUser(e)}
              />
            </Tooltip>
          )}
          {
            (agentMode && siteConfig?.shareB2C?.cashback?.is_active && diamondsCount > 0) ? (
              <Tooltip title={t_wallet.TotalDiamondsYouWillReceive}>
              <div id="obs-cart-diamonds">
                <span>💎</span>
                <span> {diamondsCount}</span>
              </div>
              </Tooltip>
            ) : null
          }
        </>
      ) : (
        <>
          <div>{t?.myVacation}</div>
        </>
      )}
    </div>
  );
};

export default ShoppingCartHeader;
